import axios, {isAxiosError} from 'axios';
import {api} from './api';
import {
    AgentCallFlow,
    ApiResponse,
    ChatMessage,
    ChatSession,
    PageResult,
    SendMessageRequest,
    SendMessageResponse,
    SessionStatus,
    TaskPlan,
    TaskPlanItem
} from '../types/chat';

const API_BASE_URL = '/api/v1/chat';
const TOKEN_KEY = 'auth_token';

// 为chat服务创建专用的axios实例，异步化后超时可降至30秒
const chatApi = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// 请求拦截器 - 添加token
chatApi.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 响应拦截器
chatApi.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // console.error('Chat API Error:', error);
        return Promise.reject(error);
    }
);

class ChatService {
    // 获取用户会话列表（分页）
    async getUserSessions(userId?: string, agentId?: number, sessionType?: string, page: number = 1, size: number = 20): Promise<PageResult<ChatSession>> {
        try {
            const params: any = {page, size};
            if (userId) {
                params.userId = userId;
            }
            if (agentId) {
                params.agentId = agentId;
            }
            if (sessionType) {
                params.sessionType = sessionType;
            }
            const response = await chatApi.get(`/sessions`, {
                params
            });
            const apiResponse: ApiResponse<PageResult<ChatSession>> = response.data;
            return apiResponse.data;
        } catch (error) {
            // console.error('获取会话列表失败:', error);
            throw error;
        }
    }

    // 创建新会话
    async createNewSession(sessionName?: string, userId?: string, agentId?: number): Promise<ChatSession> {
        try {
            const params: any = {};
            if (userId) {
                params.userId = userId;
            }
            if (sessionName) {
                params.sessionName = sessionName;
            }
            if (agentId) {
                params.agentId = agentId;
            }
            const response = await chatApi.post(`/sessions`, null, {
                params
            });
            const apiResponse: ApiResponse<ChatSession> = response.data;
            return apiResponse.data;
        } catch (error) {
            // console.error('创建会话失败:', error);
            throw error;
        }
    }

    // 获取最新会话
    async getLatestSession(userId?: string): Promise<ChatSession | null> {
        try {
            const params: any = {};
            if (userId) {
                params.userId = userId;
            }
            const response = await chatApi.get(`/sessions/latest`, {
                params
            });
            const apiResponse: ApiResponse<ChatSession> = response.data;
            return apiResponse.data;
        } catch (error) {
            if (isAxiosError(error) && error.response?.status === 404) {
                return null;
            }
            // console.error('获取最新会话失败:', error);
            throw error;
        }
    }

    // 获取会话消息
    async getSessionMessages(sessionId: number, userId?: string): Promise<ChatMessage[]> {
        try {
            const params: any = {};
            if (userId) {
                params.userId = userId;
            }
            const response = await chatApi.get(`/sessions/${sessionId}/messages`, {params});
            const apiResponse: ApiResponse<ChatMessage[]> = response.data;
            if (!apiResponse.success) {
                throw new Error(apiResponse.message || '无权访问');
            }
            return apiResponse.data;
        } catch (error) {
            throw error;
        }
    }

    // 发送消息
    async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
        try {
            const response = await chatApi.post(`/send`, request);
            const apiResponse: ApiResponse<SendMessageResponse> = response.data;
            return apiResponse.data;
        } catch (error) {
            // console.error('发送消息失败:', error);
            throw error;
        }
    }

    // 更新会话名称
    async updateSessionName(sessionId: number, sessionName: string, userId?: string): Promise<ChatSession> {
        try {
            const params: any = {sessionName};
            if (userId) {
                params.userId = userId;
            }
            const response = await chatApi.put(`/sessions/${sessionId}/name`, null, {
                params
            });
            const apiResponse: ApiResponse<ChatSession> = response.data;
            return apiResponse.data;
        } catch (error) {
            // console.error('更新会话名称失败:', error);
            throw error;
        }
    }

    // 删除会话
    async deleteSession(sessionId: number, userId?: string): Promise<void> {
        try {
            const params: any = {};
            if (userId) {
                params.userId = userId;
            }
            await chatApi.delete(`/sessions/${sessionId}`, {
                params
            });
            // 删除成功
            return;
        } catch (error) {
            // console.error('删除会话失败:', error);
            throw error;
        }
    }

    // 获取父会话下的子智能体会话列表
    async getChildSessions(sessionId: number): Promise<ChatSession[]> {
        try {
            const response = await chatApi.get(`/sessions/${sessionId}/children`);
            const apiResponse: ApiResponse<ChatSession[]> = response.data;
            return apiResponse.data || [];
        } catch (error) {
            throw error;
        }
    }

    // 查询会话状态（前端轮询用）
    async getSessionStatus(sessionId: number): Promise<SessionStatus> {
        try {
            const response = await chatApi.get(`/sessions/${sessionId}/status`);
            const apiResponse: ApiResponse<SessionStatus> = response.data;
            return apiResponse.data;
        } catch (error) {
            throw error;
        }
    }

    // 显式恢复会话（用户重新进入会话时调用）
    async resumeSession(sessionId: number): Promise<SessionStatus> {
        try {
            const response = await chatApi.post(`/sessions/${sessionId}/resume`);
            const apiResponse: ApiResponse<SessionStatus> = response.data;
            return apiResponse.data;
        } catch (error) {
            throw error;
        }
    }

    async getAgentCallFlow(sessionId: number): Promise<AgentCallFlow> {
        try {
            const response = await chatApi.get(`/sessions/${sessionId}/agent-call-flow`);
            const apiResponse: ApiResponse<AgentCallFlow> = response.data;
            return apiResponse.data;
        } catch (error) {
            throw error;
        }
    }

    // 获取任务计划详情
    async getPlan(planId: string): Promise<TaskPlan> {
        const response = await chatApi.get(`/task-plan/${planId}`);
        const apiResponse: ApiResponse<TaskPlan> = response.data;
        return apiResponse.data;
    }

    // 获取会话下的任务计划列表
    async getPlansBySession(sessionId: number): Promise<TaskPlan[]> {
        const response = await chatApi.get(`/task-plan/session/${sessionId}`);
        const apiResponse: ApiResponse<TaskPlan[]> = response.data;
        return apiResponse.data || [];
    }

    // 获取计划待办项
    async getPlanItems(planId: string): Promise<TaskPlanItem[]> {
        const response = await chatApi.get(`/task-plan/${planId}/items`);
        const apiResponse: ApiResponse<TaskPlanItem[]> = response.data;
        return apiResponse.data || [];
    }

    // 健康检查
    async healthCheck(): Promise<{ status: string; timestamp: number; service: string }> {
        try {
            const response = await api.get(`/health`);
            const apiResponse: ApiResponse<{ status: string; timestamp: number; service: string }> = response.data;
            return apiResponse.data;
        } catch (error) {
            // console.error('健康检查失败:', error);
            throw error;
        }
    }
}

export default new ChatService();
